import { useMemo, useState } from "react";
import { Copy } from "lucide-react";
import { Button, SegmentedControl, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, StatusDot, cn } from "@fox1ck-org/ui";
import { MatrixHeatmap } from "@fox1ck-org/ui/charts";
import { LineChart } from "@fox1ck-org/ui/charts/recharts";
import { toast } from "@fox1ck-org/ui/toast";
import { DimensionFilterOp } from "@gen/tracker/v1/reporting_pb";

import { DASH, formatInt, formatMoney, formatPct } from "@/lib/format";
import { aggregate, derive, type Bag } from "@/lib/campaigns-table";
import { daysIn } from "@/lib/period";
import { buildTimeGrid, extremeWindows, hh, keepWindow, toBag, weekendVsWeekdays, worstRun, type TimeGrid } from "@/lib/report";
import { useT, type TKey } from "@/lib/i18n";
import { useReport } from "@/lib/services/report-service";
import type { ReportingSchema } from "@/lib/services/reporting-schema-service";
import { useDimensionNames } from "./names";
import type { ReportEngine } from "./engine";

type Measure = "profit" | "cr" | "leads" | "clicks";
const MEASURES: Measure[] = ["profit", "cr", "leads", "clicks"];
const WD: TKey[] = ["wd.1", "wd.2", "wd.3", "wd.4", "wd.5", "wd.6", "wd.7"];
const WD_LONG: TKey[] = ["rep.wd.1", "rep.wd.2", "rep.wd.3", "rep.wd.4", "rep.wd.5", "rep.wd.6", "rep.wd.7"];
const WEEKDAYS = [0, 1, 2, 3, 4];
const WEEKEND = [5, 6];

/** Profit per hour of a group of weekdays, summed through the registry. Pure. */
export function hourProfit(grid: TimeGrid, days: readonly number[], schema: ReportingSchema): (number | undefined)[] {
  return Array.from({ length: 24 }, (_, h) => {
    const bags = days.flatMap((d) => (grid.cell[d]?.[h] ? [grid.cell[d]![h]!] : []));
    return bags.length ? aggregate(bags, schema).profit : undefined;
  });
}

/**
 * «По гео и часам» (доска ReportTime): day of week × hour on the package's
 * MatrixHeatmap, three readings of it, the Facebook schedule without the
 * hours that lose («Скопировать для Facebook»), the countries and the days.
 * Every cell is the server's (weekday × hour with subtotals, conversions by
 * the click's time); the readings sum hours through the registry's formulas.
 */
export function TimeReport({ engine }: { engine: ReportEngine }) {
  const t = useT();
  const { schema, state } = engine;
  const p = state.params;
  const [measure, setMeasure] = useState<Measure>("profit");
  const days = daysIn({ from: p.from, to: p.to });
  const country = state.filters.dims.find((f) => f.key === "country" && f.op === DimensionFilterOp.IN && f.values.length === 1)?.values[0] ?? "";

  const ready = !!schema && !!engine.streamIds && !engine.noCampaigns;
  const base = { from: p.from, to: p.to, timezone: p.tz, streamIds: engine.streamIds, basis: engine.basis, subtotals: false } as const;
  const hours = useReport({ ...base, dims: ["hour"], filters: engine.query, limit: 24 }, ready);
  // The countries are the choice, so their own list is not narrowed by it.
  const withoutCountry = { dims: engine.query.dims.filter((f) => !(f.key === "country" && f.values.length === 1 && f.values[0] === country)), metrics: engine.query.metrics };
  const countries = useReport({ ...base, dims: ["country"], filters: withoutCountry, sort: { key: "profit", desc: true }, limit: 30 }, ready);
  const countryNames = useDimensionNames((countries.data?.rows ?? []).map((r) => ["country", r.dimensionPath[0] ?? ""] as const));

  const grid = useMemo(() => (engine.main.data && hours.data ? buildTimeGrid(engine.main.data.rows, hours.data.rows) : undefined), [engine.main.data, hours.data]);

  if (!schema || !grid) {
    return (
      <div className="flex min-h-0 flex-1 gap-3">
        <Skeleton className="min-h-0 flex-1 rounded-lg" />
        <Skeleton className="w-100 rounded-lg" />
      </div>
    );
  }

  const bag = (b: Bag | undefined) => (b ? derive(schema.metrics, b) : undefined);
  const values = grid.cell.map((row) => row.map((b) => bag(b)?.[measure] ?? null));
  const fmt = (v: number | undefined | null) => (v === null || v === undefined ? DASH : measure === "profit" ? formatMoney(v, { signed: true }) : measure === "cr" ? formatPct(v) : formatInt(v));
  const total = aggregate(grid.hour.filter((b): b is Bag => !!b), schema);

  // --- readings -----------------------------------------------------------------
  const allHours = grid.hour.map((b) => bag(b)?.profit);
  const worst = worstRun(allHours);
  const worstBag = worst ? aggregate(Array.from({ length: worst.len }, (_, i) => grid.hour[(worst.from + i) % 24]).filter((b): b is Bag => !!b), schema) : undefined;
  const { best } = extremeWindows(grid.hour, schema);
  const bestDay = best
    ? [0, 1, 2, 3, 4, 5, 6].reduce((acc, d) => {
        const v = aggregate(grid.cell[d]!.slice(best.from, best.to + 1).filter((b): b is Bag => !!b), schema).profit ?? -Infinity;
        return v > acc.v ? { d, v } : acc;
      }, { d: -1, v: -Infinity }).d
    : -1;
  const ww = weekendVsWeekdays(grid.weekday, schema);
  const readings: { tone: "danger" | "ok" | "warn"; title: string; sub: string }[] = [];
  if (worst && worstBag) {
    readings.push({
      tone: "danger",
      title: t("rep.time.worst", { from: hh(worst.from), to: hh((worst.from + worst.len) % 24) }),
      sub: t("rep.time.worst.sub", { loss: formatMoney(-worst.sum), days, cr: formatPct(worstBag.cr), avg: formatPct(total.cr) }),
    });
  }
  if (best && (best.v.profit ?? 0) > 0) {
    const share = total.profit && total.profit > 0 ? (best.v.profit ?? 0) / total.profit : undefined;
    readings.push({
      tone: "ok",
      title: t(bestDay >= 0 ? "rep.time.best.day" : "rep.time.best", { from: hh(best.from), to: hh((best.to + 1) % 24), day: bestDay >= 0 ? t(WD_LONG[bestDay]!).toLowerCase() : "" }),
      sub: t("rep.time.best.sub", { share: formatPct(share, { digits: 0 }), hours: formatPct((best.to - best.from + 1) / 24, { digits: 0 }) }),
    });
  }
  if (ww.weekend.cr !== undefined && ww.weekdays.cr !== undefined && ww.weekdays.cr > 0) {
    const rel = ww.weekend.cr / ww.weekdays.cr - 1;
    readings.push({
      tone: rel < -0.1 ? "warn" : "ok",
      title: t(rel < 0 ? "rep.time.weekend.weaker" : "rep.time.weekend.stronger", { pct: formatPct(Math.abs(rel), { digits: 0 }) }),
      sub: t("rep.time.weekend.sub", { cr: formatPct(ww.weekend.cr), avg: formatPct(ww.weekdays.cr) }),
    });
  }

  // --- the schedule for Facebook ----------------------------------------------------
  const cutWeek = worstRun(hourProfit(grid, WEEKDAYS, schema));
  const cutEnd = worstRun(hourProfit(grid, WEEKEND, schema));
  const line = (label: string, run: typeof cutWeek) => {
    const keep = keepWindow(run);
    return `${label} ${keep ? `${hh(keep.from)} – ${hh(keep.to)}` : t("rep.time.allDay")}`;
  };
  const schedule = [line(`${t("wd.1")}–${t("wd.5")}`, cutWeek), line(`${t("wd.6")}–${t("wd.7")}`, cutEnd)];
  const saved = -((cutWeek?.sum ?? 0) + (cutEnd?.sum ?? 0));
  const savedWeek = days > 0 ? (saved * 7) / days : 0;

  const dayRows = [...(engine.daily.data?.rows ?? [])]
    .sort((a, b) => (a.dimensionPath[0] ?? "").localeCompare(b.dimensionPath[0] ?? ""))
    .map((r) => {
      const v = bag(toBag(r.stats)) ?? {};
      const d = r.dimensionPath[0] ?? "";
      return { day: `${d.slice(8, 10)}.${d.slice(5, 7)}`, clicks: v.clicks ?? 0, leads: v.leads ?? 0, profit: v.profit ?? 0 };
    });

  return (
    <div className="flex min-h-0 flex-1 gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <section className="flex shrink-0 flex-col rounded-lg border border-border bg-card">
          <header className="flex h-11 shrink-0 items-center gap-3 border-b border-border px-3.5">
            <h2 className="text-section-title">{t(`rep.time.title.${measure}` as TKey)}</h2>
            <span className="flex-1" />
            <SegmentedControl size="sm" aria-label={t("rep.time.measure")} value={measure} onValueChange={setMeasure} options={MEASURES.map((m) => ({ value: m, label: t(`rep.series.short.${m}` as TKey) }))} />
            <Select
              value={country || "*"}
              onValueChange={(v) => {
                const rest = state.filters.dims.filter((f) => !(f.key === "country" && f.values.length === 1 && f.values[0] === country));
                state.setFilters({ ...state.filters, dims: v === "*" ? rest : [...rest, { key: "country", op: DimensionFilterOp.IN, values: [v] }] });
              }}
            >
              <SelectTrigger className="h-8 w-40" aria-label={t("rep.time.country")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="*">{t("rep.time.allCountries")}</SelectItem>
                {(countries.data?.rows ?? []).map((r) => {
                  const c = r.dimensionPath[0] ?? "";
                  return c ? (
                    <SelectItem key={c} value={c}>
                      {countryNames.name("country", c)}
                    </SelectItem>
                  ) : null;
                })}
              </SelectContent>
            </Select>
          </header>
          <div className="p-3.5">
            <MatrixHeatmap
              label={t(`rep.time.title.${measure}` as TKey)}
              rows={WD.map((k) => t(k))}
              columns={Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0"))}
              values={values}
              scale={measure === "profit" ? "diverging" : "sequential"}
              columnHeader={(label, i) => (i % 3 === 0 ? label : "")}
              formatValue={(v) => fmt(v)}
              emptyLabel={t("rep.time.noData")}
              cellHeight={28}
              legend={{
                low: measure === "profit" ? t("rep.time.loss") : "0",
                high: measure === "profit" ? t("rep.time.gain", { days }) : t(`rep.series.short.${measure}` as TKey),
              }}
              renderTooltip={(cell) => {
                const v = bag(grid.cell[cell.row]?.[cell.column]);
                return (
                  <div className="flex min-w-45 flex-col gap-1 text-xs">
                    <b className="text-sm font-semibold">
                      {t(WD_LONG[cell.row]!)}, {hh(cell.column)}–{hh((cell.column + 1) % 24)}
                    </b>
                    <span className="text-muted-foreground">{t("rep.time.tip.sub", { days })}</span>
                    {(
                      [
                        ["rep.series.short.profit", formatMoney(v?.profit, { signed: true }), (v?.profit ?? 0) > 0 ? "text-ok" : (v?.profit ?? 0) < 0 ? "text-destructive" : ""],
                        ["rep.series.short.leads", formatInt(v?.leads), ""],
                        ["rep.series.short.cr", formatPct(v?.cr), ""],
                        ["rep.series.short.clicks", formatInt(v?.clicks), ""],
                      ] as const
                    ).map(([k, val, tone]) => (
                      <span key={k} className="flex justify-between gap-6">
                        <span>{t(k)}</span>
                        <span className={cn("font-mono", tone)}>{val}</span>
                      </span>
                    ))}
                  </div>
                );
              }}
            />
          </div>
        </section>
        <section className="flex min-h-0 flex-1 flex-col rounded-lg border border-border bg-card">
          <header className="flex h-11 shrink-0 items-center border-b border-border px-3.5">
            <h2 className="text-section-title">{t("rep.time.byDay")}</h2>
          </header>
          <div className="min-h-0 flex-1 px-2 pb-2">
            <LineChart
              data={dayRows}
              xKey="day"
              height={210}
              variant="line"
              showLegend
              lines={[
                { key: "clicks", label: t("rep.series.short.clicks") },
                { key: "leads", label: t("rep.series.short.leads") },
                { key: "profit", label: t("rep.series.short.profit") },
              ]}
              valueFormatter={(v) => formatInt(v)}
            />
          </div>
        </section>
      </div>

      <aside className="flex w-100 shrink-0 flex-col gap-3 overflow-y-auto">
        <section className="rounded-lg border border-border bg-card">
          <header className="flex h-11 items-center border-b border-border px-3.5">
            <h2 className="text-section-title">{t("rep.time.readings")}</h2>
          </header>
          {readings.length === 0 ? <p className="px-3.5 py-3 text-xs text-muted-foreground">{t("rep.time.readings.none")}</p> : null}
          {readings.map((r) => (
            <div key={r.title} className="flex items-start gap-2.5 border-b border-border px-3.5 py-2.5 last:border-b-0">
              <StatusDot tone={r.tone} className="mt-1.5" />
              <div className="min-w-0">
                <div className="text-sm font-medium">{r.title}</div>
                <div className="text-xs text-muted-foreground">{r.sub}</div>
              </div>
            </div>
          ))}
        </section>

        <section className="rounded-lg border border-border bg-card">
          <header className="flex h-11 items-center border-b border-border px-3.5">
            <h2 className="text-section-title">{t("rep.time.schedule")}</h2>
          </header>
          <div className="flex flex-col gap-2.5 p-3.5">
            <div className="rounded-md border border-border bg-surface-2 px-3 py-2.5 font-mono text-sm leading-relaxed">
              {schedule.map((s) => (
                <div key={s}>{s}</div>
              ))}
              <div className="text-xs text-muted-foreground">{t("rep.time.zone", { tz: p.tz })}</div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                disabled={!cutWeek && !cutEnd}
                onClick={() => {
                  void navigator.clipboard?.writeText([...schedule, t("rep.time.zone", { tz: p.tz })].join("\n"));
                  toast.success(t("rep.time.copied"));
                }}
              >
                <Copy className="size-3.5" strokeWidth={1.5} />
                {t("rep.time.copy")}
              </Button>
              <span className="text-xs text-muted-foreground">{saved > 0 ? t("rep.time.saves", { usd: formatMoney(savedWeek) }) : t("rep.time.nothingToCut")}</span>
            </div>
          </div>
        </section>

        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
          <header className="flex h-11 shrink-0 items-center border-b border-border px-3.5">
            <h2 className="text-section-title">{t("rep.time.byCountry")}</h2>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-2 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  {(["rep.time.col.country", "rep.series.short.clicks", "rep.series.short.leads", "rep.series.short.profit", "rep.col.roi"] as TKey[]).map((k, i) => (
                    <th key={k} className={cn("px-3 py-2 font-semibold", i === 0 ? "text-left" : "text-right")}>
                      {t(k)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(countries.data?.rows ?? []).map((r) => {
                  const c = r.dimensionPath[0] ?? "";
                  const v = bag(toBag(r.stats)) ?? {};
                  return (
                    <tr key={c} className={cn("border-t border-border", c === country && "bg-primary-soft/40")}>
                      <td className="px-3 py-1.5">{countryNames.name("country", c)}</td>
                      <Num v={formatInt(v.clicks)} />
                      <Num v={formatInt(v.leads)} />
                      <Num v={formatMoney(v.profit, { signed: true })} tone={v.profit} />
                      <Num v={formatPct(v.roi, { digits: 0, signed: true })} tone={v.roi} />
                    </tr>
                  );
                })}
              </tbody>
              {countries.data?.totals ? (
                <tfoot className="sticky bottom-0 bg-card">
                  {(() => {
                    const v = bag(toBag(countries.data.totals)) ?? {};
                    return (
                      <tr className="border-t-2 border-border font-semibold">
                        <td className="px-3 py-2">{t("rep.total.short")}</td>
                        <Num v={formatInt(v.clicks)} />
                        <Num v={formatInt(v.leads)} />
                        <Num v={formatMoney(v.profit, { signed: true })} tone={v.profit} />
                        <Num v={formatPct(v.roi, { digits: 0, signed: true })} tone={v.roi} />
                      </tr>
                    );
                  })()}
                </tfoot>
              ) : null}
            </table>
          </div>
        </section>
      </aside>
    </div>
  );
}

function Num({ v, tone }: { v: string; tone?: number }) {
  return <td className={cn("px-3 py-1.5 text-right font-mono tabular-nums", tone !== undefined && tone > 0 && "text-ok", tone !== undefined && tone < 0 && "text-destructive")}>{v}</td>;
}
